import type { JsonObject } from "@types";
import { getOwnProperty } from "@utils/getOwnProperty";
import {
  lazy,
  memo,
  Suspense,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useIsDarkMode } from "@/hooks/useIsDarkMode";
import { cn } from "@/lib/utils";

// =============================================================================
// LORDICON LOCAL ICON REGISTRY - System Regular Style
// =============================================================================

// All locally owned Lordicon files share this Lottie document contract. The
// player package exposes icon data as `any`, so keep the known document fields
// here while treating nested animation instructions as JSON data.
export interface LordiconIconData {
  readonly assets: readonly JsonObject[];
  readonly ddd: number;
  readonly fr: number;
  readonly h: number;
  readonly ip: number;
  readonly layers: readonly JsonObject[];
  readonly markers: readonly JsonObject[];
  readonly nm: string;
  readonly op: number;
  readonly props: JsonObject;
  readonly v: string;
  readonly w: number;
}

type IconLoader = () => Promise<{ default: LordiconIconData }>;

// The player (lottie-web) and every animation JSON load lazily so they stay
// out of the entry chunk — this file only ships tiny loader closures. Vite
// code-splits each JSON file into its own chunk, fetched on first use.
const LazyLordiconPlayer = lazy(() =>
  import("./lordicon-player").then((m) => ({ default: m.LordiconPlayer })),
);
const LazyNavLordiconPlayer = lazy(() =>
  import("./lordicon-player").then((m) => ({ default: m.NavLordiconPlayer })),
);

// Loaders for downloaded Lordicon JSON files
const homeIcon = () => import("@/assets/icons/lordicon/home.json");
const analyticsIcon = () =>
  import(
    "@/assets/icons/lordicon/system-regular-10-analytics-hover-analytics.json"
  );
const settingsSliderIcon = () =>
  import(
    "@/assets/icons/lordicon/system-regular-109-slider-toggle-settings-hover-slider.json"
  );
const boltIcon = () =>
  import("@/assets/icons/lordicon/system-regular-117-bolt-hover-bolt.json");
const celebrationIcon = () =>
  import(
    "@/assets/icons/lordicon/system-regular-134-celebration-hover-celebration.json"
  );
const storeIcon = () =>
  import("@/assets/icons/lordicon/system-regular-153-store-hover-store.json");
const schoolIcon = () =>
  import("@/assets/icons/lordicon/system-regular-167-school-hover-school.json");
const callMadeIcon = () =>
  import(
    "@/assets/icons/lordicon/system-regular-179-call-made-hover-call-made.json"
  );
const dialpadIcon = () =>
  import(
    "@/assets/icons/lordicon/system-regular-188-Dialpad-hover-dailpad.json"
  );
const bookIcon = () =>
  import("@/assets/icons/lordicon/system-regular-19-book-hover-book.json");
const mailOpenIcon = () =>
  import(
    "@/assets/icons/lordicon/system-regular-190-mail-envelope-open-hover-mail-open.json"
  );
const forumIcon = () =>
  import("@/assets/icons/lordicon/system-regular-192-forum-hover-forum.json");
const buildIcon = () =>
  import("@/assets/icons/lordicon/system-regular-22-build-hover-build.json");
const calendarIcon = () =>
  import(
    "@/assets/icons/lordicon/system-regular-23-calendar-hover-calendar.json"
  );
const calendarWeekIcon = () =>
  import(
    "@/assets/icons/lordicon/system-outline-3110-grid-week-hover-pinch.json"
  );
const viewGridIcon = () =>
  import("@/assets/icons/lordicon/system-regular-27-view-4-hover-view-4.json");
const pieChartIcon = () =>
  import(
    "@/assets/icons/lordicon/system-regular-43-pie-chart-diagram-hover-pie-chart.json"
  );
const bellIcon = () =>
  import(
    "@/assets/icons/lordicon/system-regular-46-notification-bell-hover-bell.json"
  );
const errorIcon = () =>
  import("@/assets/icons/lordicon/system-regular-55-error-hover-error-1.json");
const warningIcon = () =>
  import(
    "@/assets/icons/lordicon/system-regular-56-warning-hover-warning.json"
  );
const phoneIcon = () =>
  import(
    "@/assets/icons/lordicon/system-regular-58-call-phone-hover-phone-ring.json"
  );
const emailIcon = () =>
  import("@/assets/icons/lordicon/system-regular-59-email-hover-email.json");
const settingsCogIcon = () =>
  import(
    "@/assets/icons/lordicon/system-regular-63-settings-cog-hover-cog-1.json"
  );
const clockIcon = () =>
  import("@/assets/icons/lordicon/system-regular-67-clock-hover-clock.json");
const accountIcon = () =>
  import("@/assets/icons/lordicon/system-regular-8-account-hover-pinch.json");
const locationIcon = () =>
  import("@/assets/icons/lordicon/system-regular-89-location-hover-spin.json");
const fieldSalesIcon = () =>
  import("@/assets/icons/lordicon/system-regular-89-location-field-sales.json");
const helpCenterIcon = () =>
  import("@/assets/icons/lordicon/system-regular-140-help-center.json");
const verifiedIcon = () =>
  import("@/assets/icons/lordicon/system-regular-126-verified.json");
const starIcon = () =>
  import("@/assets/icons/lordicon/system-regular-95-star-hover-pinch.json");
const groupsIcon = () =>
  import("@/assets/icons/lordicon/system-regular-96-groups-hover-groups.json");
const globeIcon = () => import("@/assets/icons/lordicon/pkagxqjy-globe.json");
const extensionIcon = () => import("@/assets/icons/lordicon/extension.json");
const searchIcon = () => import("@/assets/icons/lordicon/search.json");
const superInfoIcon = () => import("@/assets/icons/lordicon/super-info.json");
const callMadeNewIcon = () => import("@/assets/icons/lordicon/call-made.json");
const sparklesIcon = () => import("@/assets/icons/lordicon/sparkles.json");
const chatWidgetIcon = () => import("@/assets/icons/lordicon/chat-widget.json");
const trendingUpIcon = () =>
  import(
    "@/assets/icons/lordicon/system-regular-160-trending-up-hover-trend-up.json"
  );
const boltAnimatedIcon = boltIcon;

// Solid icons for active states
const homeSolidIcon = () =>
  import("@/assets/icons/lordicon/system-solid-41-home-hover-pinch.json");
const forumSolidIcon = () =>
  import("@/assets/icons/lordicon/system-solid-192-forum-hover-forum.json");
const groupsSolidIcon = () =>
  import("@/assets/icons/lordicon/system-solid-96-groups-hover-groups.json");
const viewGridSolidIcon = () =>
  import("@/assets/icons/lordicon/system-solid-27-view-4-hover-view-4.json");
const celebrationSolidIcon = () =>
  import(
    "@/assets/icons/lordicon/system-solid-134-celebration-hover-celebration.json"
  );
const schoolSolidIcon = () =>
  import("@/assets/icons/lordicon/system-solid-167-school-hover-school.json");
// Derived, not downloaded — Lordicon's System Solid pack has no twin for
// System Regular #58. Built by scripts/derive-solid-phone-icon.mjs, which
// documents the two edits and why the file keeps the regular icon's `nm`.
const phoneSolidIcon = () =>
  import(
    "@/assets/icons/lordicon/system-solid-58-call-phone-hover-phone-ring.json"
  );

// Icon registry - maps keys to lazy JSON loaders
const iconRegistry = {
  // Main Navigation
  home: homeIcon,
  messages: forumIcon,
  users: groupsIcon,
  building: storeIcon,
  more: viewGridIcon,
  map: fieldSalesIcon, // Field Sales - Lordicon innuazqa (location, System Regular)
  help: helpCenterIcon, // Help center - Lordicon biqqsrac (System Regular)
  super: verifiedIcon, // Super hub - Lordicon gbycmzod (verified, System Regular)

  // Company Submenu
  store: storeIcon,
  clock: clockIcon,
  calendar: calendarIcon,
  // Dispatch - week-grid calendar. Deliberately a different glyph from
  // `calendar` (Booking Wizard): both sit in the More menu and two
  // entries sharing a key render the same animated icon.
  "calendar-week": calendarWeekIcon,
  "map-pin": locationIcon,
  star: starIcon,
  chart: pieChartIcon,
  dialpad: dialpadIcon,
  message: emailIcon,
  contact: callMadeNewIcon, // AI Transfer Destinations - call made icon

  // Services Submenu
  wrench: buildIcon,
  target: celebrationIcon, // Campaigns - confetti icon
  graduation: schoolIcon,
  puzzle: extensionIcon,
  "user-settings": settingsCogIcon,
  tags: analyticsIcon,
  file: bookIcon,
  "credit-card": storeIcon,
  briefcase: storeIcon,
  trending: trendingUpIcon, // Coach Overview - trending up icon
  "git-branch": callMadeIcon,
  "users-group": groupsIcon,
  phone: phoneIcon,

  // Account Submenu
  "user-cog": settingsCogIcon,

  // Coach Submenu
  "user-check": accountIcon,

  // More Dropdown
  settings: settingsCogIcon,
  logout: callMadeIcon,
  user: accountIcon,
  sparkles: sparklesIcon,
  "chat-widget": chatWidgetIcon, // Chat Widget - Lordicon bpptgtfr (System Regular)
  globe: globeIcon, // Website - Lordicon pkagxqjy (System Regular)
  "bolt-animated": boltAnimatedIcon, // Ask AI - animated bolt icon
  bell: bellIcon,
  "land-plot": analyticsIcon,
  // Super Admin
  notebook: bookIcon,
  database: viewGridIcon,
  activity: analyticsIcon, // Monitoring
  search: searchIcon, // Semantic Search
  "super-info": superInfoIcon, // Company Info

  // Additional
  download: callMadeIcon,
  expand: viewGridIcon,
  gauge: analyticsIcon,
  "message-circle": forumIcon,
  alert: warningIcon,
  error: errorIcon,
  "mail-open": mailOpenIcon,
  bolt: boltIcon,
  slider: settingsSliderIcon,
} satisfies Record<string, IconLoader>;

// Solid icon registry - maps keys to solid variants for active states
const solidIconRegistry = {
  home: homeSolidIcon,
  messages: forumSolidIcon,
  users: groupsSolidIcon,
  building: homeSolidIcon, // Using home solid as fallback
  more: viewGridSolidIcon,
  target: celebrationSolidIcon,
  graduation: schoolSolidIcon, // Coach
  phone: phoneSolidIcon, // Phones — derived (see phoneSolidIcon above)
} satisfies Record<string, IconLoader>;

/**
 * Stable identity of the animation each registry key renders. Several keys are
 * deliberate aliases for one JSON (`store` / `building` / `briefcase` all load
 * the store icon), so two nav entries can carry different `lordiconKey`s and
 * still draw the same glyph. Keys sharing a loader share an id here, which is
 * what lets the nav guard test compare glyphs rather than key spellings.
 */
const glyphIdByKey: ReadonlyMap<string, string> = (() => {
  const byKey = new Map<string, string>();
  const canonicalKeyForLoader = new Map<IconLoader, string>();
  for (const [key, loader] of Object.entries(iconRegistry)) {
    const canonical = canonicalKeyForLoader.get(loader);
    if (canonical === undefined) {
      canonicalKeyForLoader.set(loader, key);
      byKey.set(key, key);
    } else {
      byKey.set(key, canonical);
    }
  }
  return byKey;
})();

/**
 * The glyph a `lordiconKey` resolves to, or `undefined` when unregistered.
 * Equal ids mean identical artwork. Exported for the nav uniqueness guard.
 */
// biome-ignore lint/style/useComponentExportOnlyModules: The guard must read this runtime registry; a duplicate test list could drift.
export function lordiconGlyphId(lordiconKey: string): string | undefined {
  return glyphIdByKey.get(lordiconKey);
}

/**
 * Keys with a registered animated Lordicon (i.e. that render the house-style
 * glyph rather than silently falling back to their Lucide load icon). Exported
 * for the nav-icon guard test so a new nav tab whose `lordiconKey` isn't
 * registered fails CI — mirrors the Greptile `nav-tabs-lordicon-system-regular`
 * review rule. Read-only snapshot of the registry keys.
 */
// biome-ignore lint/style/useComponentExportOnlyModules: The guard must read this runtime registry; a duplicate test list could drift.
export const registeredLordiconKeys: readonly string[] =
  Object.keys(iconRegistry);

export type LordiconRef = {
  play: () => void;
  playFromBeginning: () => void;
};

type LordiconProps = {
  /** Icon name from registry or CDN icon ID */
  icon: string;
  /** Size in pixels (default: 24) */
  size?: number;
  /** Primary and secondary colors (hex without #) */
  colors?: {
    primary?: string;
    secondary?: string;
  };
  /** Custom className for the container */
  className?: string;
  /** Fallback component when icon not found */
  fallback?: React.ReactNode;
  /** Imperative handle exposing play / playFromBeginning */
  ref?: React.Ref<LordiconRef>;
};

// =============================================================================
// HOOK TO LOAD ICON DATA
// =============================================================================

// Loaded animation data, cached per loader so aliases sharing a JSON file and
// repeated mounts render synchronously after the first fetch.
const iconDataCache = new Map<IconLoader, LordiconIconData>();

function useIconData(loader: IconLoader | undefined): LordiconIconData | null {
  const [, forceRender] = useState(0);

  useEffect(() => {
    if (!loader || iconDataCache.has(loader)) return;
    // Warm the player chunk in parallel with the icon-data fetch so first
    // use doesn't pay a sequential data-then-player waterfall.
    void import("./lordicon-player");
    let cancelled = false;
    loader()
      .then((mod) => {
        iconDataCache.set(loader, mod.default);
        if (!cancelled) forceRender((n) => n + 1);
      })
      .catch(() => {
        // Chunk fetch failed (e.g. offline) — keep rendering the fallback.
      });
    return () => {
      cancelled = true;
    };
  }, [loader]);

  const cached = loader ? iconDataCache.get(loader) : undefined;
  return cached ?? null;
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export const Lordicon = memo(
  ({
    icon,
    size = 24,
    colors,
    className,
    fallback = null,
    ref,
  }: LordiconProps) => {
    const loader = getOwnProperty(iconRegistry, icon);
    const iconData = useIconData(loader);
    const innerRef = useRef<LordiconRef | null>(null);
    // Imperative calls made before the lazy player mounts (e.g. a toast's
    // auto-play right after render) are queued in order and flushed on mount.
    const pendingCallsRef = useRef<(keyof LordiconRef)[]>([]);

    // Expose methods via ref
    useImperativeHandle(ref, () => ({
      play: () => {
        if (innerRef.current) innerRef.current.play();
        else pendingCallsRef.current.push("play");
      },
      playFromBeginning: () => {
        if (innerRef.current) innerRef.current.playFromBeginning();
        else pendingCallsRef.current.push("playFromBeginning");
      },
    }));

    const handleRef = useCallback((handle: LordiconRef | null) => {
      innerRef.current = handle;
      if (handle) {
        for (const method of pendingCallsRef.current) {
          handle[method]();
        }
        pendingCallsRef.current = [];
      }
    }, []);

    // Show fallback when the icon is not in the registry
    if (!loader) {
      if (fallback) return <>{fallback}</>;
      return null;
    }

    // Same-size container reserves space while the player/data load, so the
    // icon popping in doesn't shift layout.
    return (
      <div
        className={cn("inline-flex items-center justify-center", className)}
        style={{ width: size, height: size }}
      >
        {iconData !== null ? (
          <Suspense fallback={null}>
            <LazyLordiconPlayer
              iconData={iconData}
              size={size}
              colors={
                colors
                  ? `primary:${colors.primary || "#121331"},secondary:${colors.secondary || "#08a88a"}`
                  : undefined
              }
              handleRef={handleRef}
            />
          </Suspense>
        ) : null}
      </div>
    );
  },
);

Lordicon.displayName = "Lordicon";

// =============================================================================
// NAV ICON COMPONENT - For use in navigation with Lucide fallback
// =============================================================================

type NavLordiconProps = {
  /** Lordicon registry key or CDN icon ID */
  lordiconKey: string;
  /**
   * Glyph family to use while `isActive`. `"solid"` (the default) swaps in the
   * solid variant when one is registered — the main nav bar's treatment. Use
   * `"outline"` on surfaces where only some entries have a solid variant, so
   * the active row doesn't render in a different family from its siblings.
   */
  activeVariant?: "solid" | "outline";
  /** Lucide icon component as fallback */
  LucideIcon: React.ComponentType<{
    className?: string;
    style?: React.CSSProperties;
  }>;
  /** Size in pixels */
  size?: number;
  /** Custom className */
  className?: string;
  /** Whether this nav item is currently active */
  isActive?: boolean;
  /** Icon colors - overrides automatic dark/light mode detection */
  colors?: {
    primary?: string;
    secondary?: string;
  };
  /** Recolor the whole icon to one CSS color, e.g. currentColor. */
  colorize?: string;
};

// Color configurations for different states.
// Default (inactive) primary should visually match `text-foreground/70` in the
// nav so icons don't look ghostly next to the labels. Active stays bold.
const ICON_COLORS = {
  light: {
    default: {
      primary: "#404040", // ~70% black — matches text-foreground/70 in light mode
      secondary: "#6b7280", // gray-500 — slightly lighter accent
    },
    active: {
      primary: "#111827", // gray-900 - bold for active
      secondary: "#374151", // gray-700
    },
  },
  dark: {
    default: {
      primary: "#cfcfcf", // ~80% white — matches text-foreground/70 in dark mode
      secondary: "#9ca3af", // gray-400 — slightly dimmer accent
    },
    active: {
      primary: "#ffffff", // white - bold for active
      secondary: "#e5e7eb", // gray-200
    },
  },
} as const;

// Super marker colors. The Super nav and info entries are amber (the app-wide
// super-only marker, `--warning-600`) rather than nav ink. These literals are
// the hex resolution of `--warning-600` per mode (light amber-600 / dark
// amber-300, matching the token's ramp-lightens-in-dark direction) — the
// Lordicon player's `primary:#hex,secondary:#hex` format only accepts hex
// literals (an `oklch()`/`rgb()` value's commas/spaces break the parser), which
// is why every entry here — like the ink colors above — is a literal, not a
// `var(--token)`. Amber is constant across rest/active, mirroring the label.
const SUPER_ICON_COLORS = {
  light: { primary: "#d97706", secondary: "#d97706" }, // --warning-600 (amber-600)
  dark: { primary: "#fcd34d", secondary: "#fcd34d" }, // --warning-600 (amber-300)
} as const;

/**
 * Whether a nav glyph renders its solid variant. Pure so surfaces that mix
 * entries with and without a registered solid icon can be guarded in tests:
 * an active row that flips to solid while its siblings stay outline reads as a
 * different icon set, not as "selected".
 */
// biome-ignore lint/style/useComponentExportOnlyModules: Pure resolver for the component below; a duplicated copy in tests would drift from the registry.
export function usesSolidGlyph(
  lordiconKey: string,
  isActive: boolean,
  activeVariant: "solid" | "outline" = "solid",
): boolean {
  if (!isActive || activeVariant === "outline") return false;
  return getOwnProperty(solidIconRegistry, lordiconKey) !== undefined;
}

export const NavLordicon = memo(
  ({
    lordiconKey,
    LucideIcon,
    size = 18,
    className,
    isActive = false,
    activeVariant = "solid",
    colors,
    colorize,
  }: NavLordiconProps) => {
    const loader = getOwnProperty(iconRegistry, lordiconKey);
    // Solid variant for the active state loads only when needed
    const solidLoader = usesSolidGlyph(lordiconKey, isActive, activeVariant)
      ? getOwnProperty(solidIconRegistry, lordiconKey)
      : undefined;
    const iconData = useIconData(loader);
    const solidIconData = useIconData(solidLoader);
    const isDarkMode = useIsDarkMode();

    const displayIconData =
      isActive && solidIconData !== null ? solidIconData : iconData;

    // Lucide fallback while loading or when the key isn't registered — same
    // footprint as the player, so the swap doesn't shift layout.
    const lucideFallback = (
      <LucideIcon
        className={cn("shrink-0", className)}
        style={{ width: size, height: size }}
      />
    );

    if (!loader || displayIconData === null) {
      return lucideFallback;
    }

    // Determine colors based on dark mode and active state
    const getIconColors = () => {
      if (colors) return colors;

      // "super-info" callers currently supply colorize so the icon inherits
      // text-warning-600. This fallback keeps the same amber if rendered
      // without colorize later.
      if (lordiconKey === "super" || lordiconKey === "super-info") {
        return isDarkMode ? SUPER_ICON_COLORS.dark : SUPER_ICON_COLORS.light;
      }

      const theme = isDarkMode ? ICON_COLORS.dark : ICON_COLORS.light;
      return isActive ? theme.active : theme.default;
    };

    const iconColors = colorize ? undefined : getIconColors();

    return (
      <Suspense fallback={lucideFallback}>
        <LazyNavLordiconPlayer
          iconData={displayIconData}
          size={size}
          colors={
            iconColors
              ? `primary:${iconColors.primary},secondary:${iconColors.secondary}`
              : undefined
          }
          colorize={colorize}
          className={className}
        />
      </Suspense>
    );
  },
);

NavLordicon.displayName = "NavLordicon";
